/**
 * TOP 7 Product Buy Box
 * Quantity stepper, gallery thumbnail swap and add-to-cart via the
 * native Eurus cart contract (xCartHelper.withCartLock -> cart/add.js ->
 * reRenderSections -> mini cart).
 * Registered on alpine:init so it re-initialises safely after theme-editor reloads.
 */
(() => {
  const register = () => {
    if (!window.Alpine || window.__top7BuyboxRegistered) return;
    window.__top7BuyboxRegistered = true;

    Alpine.data('xTop7Buybox', (config) => ({
      sectionId: (config && config.sectionId) || '',
      pullSections: (config && config.pullSections) || 0,
      loading: false,
      selectedQty: 1,
      // Own-scope declarations: without these, Alpine writes the props into a shared
      // parent scope and the bundle selector (pulled into this buybox) overwrites `root`.
      root: null, errorEl: null, liveEl: null, track: null, thumbsWrap: null, thumbs: [], activeIdx: 0,
      qtyWrap: null, qtyInput: null, totals: [], maxQty: 99,
      t7q: null, t7qPer: [], t7qSave: [], t7qMs: [], t7qPct: [], t7qWas: [],
      desktopMq: null, movedSections: [], overlaid: [], overlayIds: null, overlayMode: false,
      overlayParent: null, overlayParentStyled: false, overlayRo: null,
      onSectionLoad: null, onLayoutChange: null, onEditorChange: null, layoutOverlay: null,
      _ovRaf: 0, _trackRaf: 0,

      init() {
        this.root = this.$el;
        this.errorEl = this.root.querySelector('[data-t7pb-error]');
        this.liveEl = this.root.querySelector('[data-t7pb-live]');
        this.track = this.root.querySelector('[data-t7pb-track]');
        this.thumbsWrap = this.root.querySelector('[data-t7pb-thumbs]');
        this.thumbs = Array.from(this.root.querySelectorAll('[data-t7pb-thumb]'));
        this.activeIdx = 0;

        this.qtyWrap = this.root.querySelector('[data-t7pb-qty]');
        this.qtyInput = this.root.querySelector('[data-t7pb-qty-input]');
        try {
          this.totals = JSON.parse((this.qtyWrap && this.qtyWrap.dataset.totals) || '[]');
        } catch (err) {
          this.totals = [];
        }
        this.maxQty = parseInt(this.qtyWrap && this.qtyWrap.dataset.max, 10) || 99;
        // Premium selector data (all strings pre-formatted in Liquid).
        this.t7q = this.root.querySelector('[data-t7q]');
        try { this.t7qT = JSON.parse((this.t7q && this.t7q.dataset.i18n) || '{}'); } catch (err) { this.t7qT = {}; }
        if (this.t7q) {
          try {
            this.t7qPer = JSON.parse(this.t7q.dataset.per || '[]');
            this.t7qSave = JSON.parse(this.t7q.dataset.save || '[]');
            this.t7qMs = JSON.parse(this.t7q.dataset.ms || '[]');
            this.t7qPct = JSON.parse(this.t7q.dataset.pct || '[]');
            this.t7qWas = JSON.parse(this.t7q.dataset.was || '[]');
          } catch (err) { this.t7q = null; }
          if (this.t7q) this.updateT7q(1);
        }
        if (this.qtyInput) {
          this.qtyInput.addEventListener('input', () => {
            const v = parseInt(this.qtyInput.value, 10);
            if (v >= 1) this.setQty(v, true);
          });
          this.qtyInput.addEventListener('change', () => this.setQty(parseInt(this.qtyInput.value, 10) || 1));
          // Catalog / ad links: ?pack=3 (also ?qty=, ?bundle=, ?quantity=) opens the page with that many bottles selected.
          let urlPack = 0;
          try {
            const p = new URLSearchParams(window.location.search);
            urlPack = parseInt(p.get('pack') || p.get('qty') || p.get('bundle') || p.get('quantity'), 10) || 0;
          } catch (err) { urlPack = 0; }
          if (urlPack > 1) {
            this.setQty(urlPack);
            this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }

        // Event delegation survives theme-editor section re-renders.
        this.root.addEventListener('click', (e) => {
          if (e.target.closest('[data-t7pb-minus]')) return this.setQty(this.selectedQty - 1);
          if (e.target.closest('[data-t7pb-plus]')) return this.setQty(this.selectedQty + 1);
          const ms = e.target.closest('[data-t7q-ms]');
          if (ms) return this.setQty(parseInt(ms.dataset.t7qMs, 10) || 1);
          const thumb = e.target.closest('[data-t7pb-thumb]');
          if (thumb) return this.goTo(parseInt(thumb.dataset.index, 10) || 0);
          if (e.target.closest('[data-t7pb-prev]')) return this.goTo(this.activeIdx - 1);
          if (e.target.closest('[data-t7pb-next]')) return this.goTo(this.activeIdx + 1);
          const add = e.target.closest('[data-t7pb-add]');
          if (add) return this.addToCart(add);
        });

        // Swiping the big image (native scroll) keeps the thumbnails in sync.
        if (this.track) {
          this.track.addEventListener('scroll', () => {
            if (this._trackRaf) return;
            this._trackRaf = requestAnimationFrame(() => {
              this._trackRaf = null;
              const w = this.track.clientWidth || 1;
              const idx = Math.round(this.track.scrollLeft / w);
              if (idx !== this.activeIdx) this.setActive(idx);
            });
          }, { passive: true });
        }

        // Desktop: pull the template's next sections into the info column - in the
        // theme editor too, so the editor shows the real desktop layout.
        // The hazard this guards against: Shopify re-renders a section by replacing
        // its whole wrapper, so a foreign section nested inside this one goes with
        // it. Three layers keep that from losing anything - destroy() puts the
        // sections back at their markers while the nodes are still alive, restore
        // falls back to the held node reference, and recoverLostSections() re-fetches
        // anything that still went missing via the Section Rendering API.
        this.movedSections = [];
        // Theme editor: never nest other sections inside this one. Each section
        // carries the editor's own section attributes, and a section found inside
        // another is reported as "Broken HTML" for this file. Instead the sections
        // stay where they are in the DOM and are only POSITIONED under the CTA.
        // (?t7pb_overlay=1 forces this mode on the storefront for testing.)
        this.overlayMode = !!((window.Shopify && window.Shopify.designMode) || /[?&]t7pb_overlay=1/.test(window.location.search));
        this.overlaid = [];
        this.recoverLostSections();
        // The editor re-renders a pulled section in place (inside our slot) with a
        // fresh wrapper that carries the section class again - hide it again.
        this.onSectionLoad = (e) => {
          const id = e && e.detail && e.detail.sectionId;
          const el = id && document.getElementById('shopify-section-' + id);
          if (el && el.closest('[data-t7pb-below]')) this.markPulled(el);
        };
        document.addEventListener('shopify:section:load', this.onSectionLoad);
        this.desktopMq = window.matchMedia('(min-width: 1024px)');
        this.onLayoutChange = () => this.relocateSections();
        if (this.desktopMq.addEventListener) this.desktopMq.addEventListener('change', this.onLayoutChange);
        else if (this.desktopMq.addListener) this.desktopMq.addListener(this.onLayoutChange);
        this.relocateSections();
      },

      // Alpine calls this when the component's element leaves the DOM, so a
      // re-render never strands the listener or leaves sections in the slot.
      destroy() {
        this.stopOverlay();
        this.restoreSections();
        if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
        if (!this.desktopMq || !this.onLayoutChange) return;
        if (this.desktopMq.removeEventListener) this.desktopMq.removeEventListener('change', this.onLayoutChange);
        else if (this.desktopMq.removeListener) this.desktopMq.removeListener(this.onLayoutChange);
      },

      // Runs on every init, so it also runs after the theme editor re-renders this
      // section. A marker whose section is missing means that section was destroyed
      // with our old wrapper: ask Shopify for it again and drop it back in place.
      recoverLostSections() {
        const orphans = [];
        document.querySelectorAll('[data-t7pb-marker]').forEach((marker) => {
          const id = marker.getAttribute('data-t7pb-marker');
          if (!id) return marker.remove();
          const section = document.getElementById(id);
          if (section && section.isConnected) return; // still on the page
          orphans.push({ marker: marker, id: id });
        });
        if (!orphans.length) return;

        const sectionIds = orphans.map((o) => o.id.replace(/^shopify-section-/, ''));
        fetch(window.location.pathname + '?sections=' + encodeURIComponent(sectionIds.join(',')))
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
          .then((data) => {
            orphans.forEach((o, i) => {
              const html = data[sectionIds[i]];
              if (!html || !o.marker.parentNode) return o.marker.remove();
              const holder = document.createElement('div');
              holder.innerHTML = html;
              const fresh = holder.firstElementChild;
              if (!fresh) return o.marker.remove();
              o.marker.parentNode.replaceChild(fresh, o.marker);
              // Let the section's own scripts (sliders etc.) boot.
              fresh.dispatchEvent(new CustomEvent('shopify:section:load', {
                detail: { sectionId: sectionIds[i] }, bubbles: true
              }));
            });
            this.relocateSections();
          })
          .catch(() => {
            // Recovery is best effort: never leave a stray marker behind.
            orphans.forEach((o) => o.marker.remove());
          });
      },

      relocateSections() {
        if (!this.pullSections) return;
        const slot = this.root.querySelector('[data-t7pb-below]');
        if (!slot) return;
        if (this.overlayMode) return this.desktopMq.matches ? this.startOverlay() : this.stopOverlay();
        if (!this.desktopMq.matches) return this.restoreSections(); // mobile: document order
        if (this.movedSections.length) return;                      // already moved

        const ownWrapper = this.root.closest('.shopify-section');
        if (!ownWrapper || !ownWrapper.parentNode) return;
        let el = ownWrapper.nextElementSibling;
        let moved = 0;
        while (el && moved < this.pullSections) {
          const next = el.nextElementSibling;
          // An id is required: it is how the section is found again on the way back.
          if (el.classList.contains('shopify-section') && el.id) {
            const marker = document.createElement('div');
            marker.id = el.id + '--t7pb-home';
            marker.setAttribute('data-t7pb-marker', el.id);
            marker.hidden = true;
            el.parentNode.insertBefore(marker, el);
            slot.appendChild(el);
            this.markPulled(el);
            this.movedSections.push({ id: el.id, markerId: marker.id, node: el });
            moved++;
          }
          el = next;
        }
        // Sliders inside the moved sections need a resize nudge to re-measure.
        if (moved) window.dispatchEvent(new Event('resize'));
      },

      // ---- Theme-editor overlay: position, don't move -----------------------
      startOverlay() {
        const ownWrapper = this.root.closest('.shopify-section');
        const slot = this.root.querySelector('[data-t7pb-below]');
        if (!ownWrapper || !ownWrapper.parentNode || !slot) return;
        const ids = [];
        let el = ownWrapper.nextElementSibling;
        while (el && ids.length < this.pullSections) {
          if (el.classList.contains('shopify-section') && el.id) ids.push(el.id);
          el = el.nextElementSibling;
        }
        this.stopOverlay();
        this.overlayIds = ids;
        this.overlayParent = ownWrapper.parentNode;
        if (getComputedStyle(this.overlayParent).position === 'static') {
          this.overlayParent.style.position = 'relative';
          this.overlayParentStyled = true;
        }
        this.layoutOverlay = () => {
          if (this._ovRaf) return;
          this._ovRaf = requestAnimationFrame(() => {
            this._ovRaf = null;
            this.placeOverlay();
          });
        };
        this.overlayRo = window.ResizeObserver ? new ResizeObserver(this.layoutOverlay) : null;
        ids.forEach((id) => {
          const sec = document.getElementById(id);
          if (!sec) return;
          sec.classList.add('t7pb-overlaid');
          if (this.overlayRo) this.overlayRo.observe(sec);
        });
        if (this.overlayRo) this.overlayRo.observe(this.root);
        window.addEventListener('resize', this.layoutOverlay);
        // The editor re-renders or reorders sections: re-collect them.
        this.onEditorChange = () => setTimeout(() => this.relocateSections(), 50);
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((ev) =>
          document.addEventListener(ev, this.onEditorChange));
        this.placeOverlay();
        window.dispatchEvent(new Event('resize'));
      },

      placeOverlay() {
        const slot = this.root.querySelector('[data-t7pb-below]');
        if (!slot || !this.overlayParent || !this.overlayIds) return;
        const secs = this.overlayIds.map((id) => document.getElementById(id)).filter(Boolean);
        const pRect = this.overlayParent.getBoundingClientRect();
        const sRect = slot.getBoundingClientRect();
        let top = sRect.top - pRect.top;
        const left = sRect.left - pRect.left;
        let total = 0;
        secs.forEach((sec) => {
          sec.classList.add('t7pb-overlaid');
          sec.style.position = 'absolute';
          sec.style.left = left + 'px';
          sec.style.width = sRect.width + 'px';
          sec.style.top = top + 'px';
          const h = sec.offsetHeight;
          top += h;
          total += h;
        });
        const want = total + 'px';
        if (slot.style.height !== want) slot.style.height = want;
      },

      stopOverlay() {
        if (this.overlayRo) this.overlayRo.disconnect();
        this.overlayRo = null;
        if (this.layoutOverlay) window.removeEventListener('resize', this.layoutOverlay);
        if (this.onEditorChange) {
          ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((ev) =>
            document.removeEventListener(ev, this.onEditorChange));
          this.onEditorChange = null;
        }
        (this.overlayIds || []).forEach((id) => {
          const sec = document.getElementById(id);
          if (!sec) return;
          sec.classList.remove('t7pb-overlaid');
          ['position', 'left', 'width', 'top'].forEach((k) => sec.style.removeProperty(k));
        });
        this.overlayIds = null;
        if (this.overlayParentStyled && this.overlayParent) this.overlayParent.style.removeProperty('position');
        this.overlayParentStyled = false;
        this.overlayParent = null;
        const slot = this.root && this.root.querySelector('[data-t7pb-below]');
        if (slot) slot.style.removeProperty('height');
      },

      // While a section sits inside the buy box it drops the 'shopify-section'
      // class: to the theme editor a section nested in a section means unclosed
      // HTML, which raised "Broken HTML has been detected" for this file. The id
      // (which every section-scoped style and the editor use) is kept as is.
      markPulled(el) {
        el.classList.remove('shopify-section');
        el.classList.add('t7pb-pulled-section');
      },

      unmarkPulled(el) {
        el.classList.remove('t7pb-pulled-section');
        el.classList.add('shopify-section');
      },

      restoreSections() {
        if (!this.movedSections.length) return;
        const ownWrapper = this.root.closest('.shopify-section');
        // Both nodes are looked up by id rather than kept as references: either one
        // can be replaced in place, and a stale reference would re-insert a detached
        // copy (or silently drop the live one).
        this.movedSections.forEach(({ id, markerId, node }) => {
          // Prefer the live node: the editor can replace a moved section in place,
          // which would make the held reference a detached copy. If the live one is
          // gone it is our own wrapper that was replaced, and the held node - still
          // intact, just detached - is the only way back.
          let section = document.getElementById(id);
          if (!section || !section.isConnected) section = node;
          const marker = document.getElementById(markerId);
          if (section) this.unmarkPulled(section);
          if (section && marker && marker.parentNode) {
            marker.parentNode.insertBefore(section, marker);
          } else if (section && ownWrapper && ownWrapper.parentNode) {
            // Marker lost: put the section back next to us rather than leave it
            // buried in the info column.
            ownWrapper.parentNode.insertBefore(section, ownWrapper.nextSibling);
          }
          if (marker) marker.remove();
        });
        this.movedSections = [];
      },

      // keepInput: while the shopper is typing, don't rewrite their field.
      setQty(qty, keepInput) {
        qty = Math.max(1, Math.min(this.maxQty, parseInt(qty, 10) || 1));
        this.selectedQty = qty;
        if (this.qtyInput && !keepInput) this.qtyInput.value = qty;
        const minus = this.root.querySelector('[data-t7pb-minus]');
        const plus = this.root.querySelector('[data-t7pb-plus]');
        if (minus) minus.disabled = qty <= 1;
        if (plus) plus.disabled = qty >= this.maxQty;
        // Totals are pre-formatted by Liquid's money filter - no client-side currency maths.
        const totalEl = this.root.querySelector('[data-t7pb-total]');
        const total = this.totals[qty - 1];
        if (totalEl && total) totalEl.textContent = total;
        if (this.liveEl) this.liveEl.textContent = qty + (total ? ' \u00b7 ' + total : '');
        if (this.t7q) this.updateT7q(qty);
      },

      updateT7q(qty) {
        const q = this.t7q;
        const per = this.t7qPer[qty - 1];
        const save = this.t7qSave[qty - 1];
        const perEl = q.querySelector('[data-t7q-per]');
        const saveEl = q.querySelector('[data-t7q-save]');
        if (perEl && per) perEl.textContent = per;
        const pctEl = q.querySelector('[data-t7q-pct]');
        const pct = this.t7qPct[qty - 1];
        if (pctEl && pct > 0) pctEl.textContent = this.t7qT.off ? this.t7qT.off.replace('__P__', pct) : pct + '% OFF';
        const wasEl = this.root.querySelector('[data-t7pb-was]');
        const was = this.t7qWas[qty - 1];
        if (wasEl && was) wasEl.textContent = was;
        if (saveEl) { saveEl.hidden = !save; saveEl.textContent = save ? (this.t7qT.save ? this.t7qT.save.replace('__A__', save) : 'You save ' + save) : ''; }
        const ms = this.t7qMs;
        let reached = 0;
        ms.forEach((m, i) => { if (qty >= m) reached = i; });
        q.querySelectorAll('[data-t7q-ms]').forEach((el, i) => {
          el.classList.toggle('is-on', i <= reached);
          el.classList.toggle('is-current', i === reached);
        });
        const fill = q.querySelector('[data-t7q-fill]');
        if (fill) {
          let frac = reached / Math.max(1, ms.length - 1);
          const next = ms[reached + 1];
          if (next) frac += ((qty - ms[reached]) / (next - ms[reached])) / Math.max(1, ms.length - 1);
          fill.style.width = Math.min(100, frac * 100) + '%';
        }
        const fs = q.querySelector('[data-t7q-fs]');
        if (fs) {
          const left = Number(fs.dataset.t7qFs) - qty;
          fs.classList.toggle('is-on', left <= 0);
          fs.querySelectorAll('[data-t7q-fs-n]').forEach(el => { el.hidden = Number(el.dataset.t7qFsN) !== left; });
        }
        const nudge = q.querySelector('[data-t7q-nudge]');
        if (nudge) {
          const next = ms[reached + 1];
          nudge.textContent = '';
          if (next) {
            const more = next - qty;
            const b = document.createElement('b');
            b.textContent = (this.t7qPer[next - 1] || '') + (this.t7qT.pershort || '/bottle');
            nudge.append(((this.t7qT.more && this.t7qT.more[more]) ? this.t7qT.more[more] : 'Add ' + more + ' more ' + (more === 1 ? 'bottle' : 'bottles')) + ' \u2192 ', b);
            const nextPct = this.t7qPct[next - 1];
            if (nextPct > 0) {
              const pill = document.createElement('span');
              pill.className = 't7q-nextpct';
              pill.textContent = '\u2212' + nextPct + '%';
              nudge.append(' ', pill);
            }
          } else {
            const b = document.createElement('b');
            b.textContent = this.t7qT.best || 'Best price unlocked';
            nudge.append('\u2713 ', b, ' \u2013 ' + (per || '') + ' ' + (this.t7qT.per || '/ bottle'));
          }
        }
      },

      goTo(idx) {
        if (!this.track) return;
        const max = this.thumbs.length - 1;
        idx = Math.max(0, Math.min(max, idx));
        this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: 'smooth' });
        this.setActive(idx);
      },

      setActive(idx) {
        const max = this.thumbs.length - 1;
        idx = Math.max(0, Math.min(max, idx));
        this.activeIdx = idx;
        this.thumbs.forEach((el, i) => el.classList.toggle('active', i === idx));
        // Sliding thumbnail window: the active thumb sits second in view when
        // possible, so moving forward uncovers the next thumb and drops the
        // first (and the reverse when moving back).
        if (this.thumbsWrap && this.thumbs.length > 1) {
          // Desktop stacks the thumbs vertically, so scroll along whichever axis they run.
          const vertical = this.thumbs[1].offsetTop !== this.thumbs[0].offsetTop;
          const step = (vertical
            ? this.thumbs[1].offsetTop - this.thumbs[0].offsetTop
            : this.thumbs[1].offsetLeft - this.thumbs[0].offsetLeft) || 1;
          const maxScroll = vertical
            ? this.thumbsWrap.scrollHeight - this.thumbsWrap.clientHeight
            : this.thumbsWrap.scrollWidth - this.thumbsWrap.clientWidth;
          const target = Math.max(0, Math.min(maxScroll, (idx - 1) * step));
          this.thumbsWrap.scrollTo(vertical ? { top: target, behavior: 'smooth' } : { left: target, behavior: 'smooth' });
        }
      },

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.classList.toggle('t7pb-error--visible', !!message);
      },

      addToCart(button) {
        if (this.loading) return;
        const variantId = button.dataset.variantId;
        const quantity = this.selectedQty || 1;
        if (!variantId) return;

        this.loading = true;
        this.showError('');
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');

        const items = [{ id: variantId, quantity: quantity }];
        const helper = window.Alpine && Alpine.store('xCartHelper');
        const miniCart = window.Alpine && Alpine.store('xMiniCart');

        const done = () => {
          this.loading = false;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        };

        const doFetch = (standardEvent) =>
          fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: items,
              sections: helper ? helper.getSectionsToRender().map((s) => s.id) : undefined
            })
          })
            .then((r) => (helper ? helper.cartJson(r) : r.json()))
            .then((response) => {
              if (response.status == '422') {
                this.showError(response.description || response.message || '');
                document.dispatchEvent(new CustomEvent('eurus:product:add-failed', {
                  detail: { errorMessage: response.description }
                }));
                if (helper) return helper.settleWithCart(standardEvent, helper.userErrorsFrom(response));
                return;
              }
              if (helper && response.sections) helper.reRenderSections(response.sections);
              if (this.liveEl) this.liveEl.textContent = 'Added to cart';
              document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: response.items || response } }));
              if (miniCart && Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
                Alpine.store('xCartNoti').setItem(response);
              } else if (miniCart) {
                miniCart.openCart();
                document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
              }
              document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
              if (helper) return helper.settleWithCart(standardEvent);
            })
            .catch((error) => {
              if (standardEvent) standardEvent.fail(error);
              this.showError(typeof error === 'string' ? error : (error && error.message) || 'Something went wrong. Please try again.');
              document.dispatchEvent(new CustomEvent('eurus:product:add-failed', { detail: { errorMessage: error } }));
              console.error('TOP7 buybox add error:', error);
            })
            .finally(done);

        if (helper && helper.withCartLock) {
          helper.withCartLock((standardEvent) => doFetch(standardEvent), {
            type: 'shopify:cart:lines-update',
            action: 'add',
            context: 'product',
            lines: helper.addLines ? helper.addLines(items) : undefined
          });
        } else {
          doFetch(null);
        }
      }
    }));
  };

  document.addEventListener('alpine:init', register);
  if (window.Alpine) register();
})();
